/**
 * Bundle cart — sync quantity and remove all lines sharing bundle_id.
 *
 * Qty changes: only paid bundle lines are updated; lines with
 * _nbbb_free_gift are left out of the payload so they stay at qty 1.
 * Remove (qty 0): every line in the bundle, including free gifts.
 */
(function () {
  'use strict';

  if (window._nbbbCartExtended) return;
  window._nbbbCartExtended = true;

  const KEY_PREFIX = 'nbbb:key:';
  const BUNDLE_PREFIX = 'nbbb:bundle:';

  const _cache = {};
  let _busy = false;
  let _refreshInFlight = null;

  function _norm(key) {
    try {
      return decodeURIComponent(String(key || '')).trim();
    } catch (_) {
      return String(key || '').trim();
    }
  }

  function _post(url, body) {
    return new Promise(function (resolve, reject) {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.onload = function () {
        let data = null;
        try {
          data = JSON.parse(xhr.responseText);
        } catch (_) {
          /* ignore */
        }
        if (xhr.status >= 200 && xhr.status < 300 && data !== null) resolve(data);
        else reject(new Error('HTTP ' + xhr.status + (data?.description ? ': ' + data.description : '')));
      };
      xhr.onerror = function () {
        reject(new Error('Network error'));
      };
      xhr.send(JSON.stringify(body));
    });
  }

  function _ensureJs(url) {
    const u = String(url || '/cart/update');
    return /\.js(\?|$)/.test(u) ? u : u.replace(/\/?$/, '') + '.js';
  }

  function _updateUrl() {
    const routes = window.themeVariables?.routes || window.routes || {};
    return _ensureJs(routes.cartUpdateUrl || routes.cart_update_url || '/cart/update');
  }

  function _getCart() {
    const routes = window.themeVariables?.routes || window.routes || {};
    const cartUrl = _ensureJs(routes.cartUrl || routes.cart_url || '/cart');
    return fetch(cartUrl, { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function _bidFromProps(properties) {
    const p = properties || {};
    return String(p.bundle_id || p._bundle_id || p['bundle_id'] || '').trim();
  }

  function _bidFromKey(itemKey) {
    const nk = _norm(itemKey);
    if (!nk) return '';
    if (_cache[nk]) return _cache[nk];
    try {
      return sessionStorage.getItem(KEY_PREFIX + nk) || '';
    } catch (_) {
      return '';
    }
  }

  function _registerKey(itemKey, bundleId) {
    const nk = _norm(itemKey);
    if (!nk || !bundleId) return;
    _cache[nk] = bundleId;
    try {
      sessionStorage.setItem(KEY_PREFIX + nk, bundleId);
    } catch (_) {
      /* ignore */
    }
  }

  function _keysForBundle(bundleId) {
    const keys = new Set();
    try {
      const raw = sessionStorage.getItem(BUNDLE_PREFIX + bundleId);
      if (raw) {
        JSON.parse(raw).forEach(function (k) {
          if (k) keys.add(_norm(k));
        });
      }
    } catch (_) {
      /* ignore */
    }
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const sk = sessionStorage.key(i);
        if (!sk || !sk.startsWith(KEY_PREFIX)) continue;
        if (sessionStorage.getItem(sk) === bundleId) {
          keys.add(sk.slice(KEY_PREFIX.length));
        }
      }
    } catch (_) {
      /* ignore */
    }
    return keys;
  }

  function _keysFromDom(bundleId) {
    const keys = new Set();
    if (!bundleId) return keys;
    try {
      document.querySelectorAll('line-item[data-bundle-id], tr.line-item[data-bundle-id]').forEach(function (row) {
        if (String(row.dataset.bundleId || '').trim() !== bundleId) return;
        const k = _norm(row.dataset.itemKey);
        if (k) keys.add(k);
      });
    } catch (_) {
      /* ignore */
    }
    return keys;
  }

  function _saveBundleKeys(bundleId, keys) {
    try {
      sessionStorage.setItem(BUNDLE_PREFIX + bundleId, JSON.stringify(keys));
    } catch (_) {
      /* ignore */
    }
  }

  function _clearBundle(bundleId) {
    try {
      sessionStorage.removeItem(BUNDLE_PREFIX + bundleId);
    } catch (_) {
      /* ignore */
    }
    _keysForBundle(bundleId).forEach(function (k) {
      try {
        sessionStorage.removeItem(KEY_PREFIX + k);
      } catch (_) {
        /* ignore */
      }
      delete _cache[k];
    });
  }

  function _bidFromItem(item) {
    if (!item) return '';
    return _bidFromProps(item.properties) || _bidFromKey(item.key);
  }

  function _isBundleFreeGift(item) {
    const p = item?.properties || {};
    return String(p._nbbb_free_gift || p['_nbbb_free_gift'] || '').trim() === 'true';
  }

  function _isCartGiftLine(item) {
    if (!item) return false;
    if (_isBundleFreeGift(item)) return true;
    const p = item.properties || {};
    if (String(p._autoGift || p['_autoGift'] || '').trim() === 'true') return true;
    if (String(p._gift || p['_gift'] || '').trim() === 'true') return true;
    const productType = String(item.product_type || '').toLowerCase();
    return productType === 'free_gift' || productType.includes('free-gift');
  }

  function _isGiftRow(el) {
    const row = el?.closest?.('line-item, tr.line-item');
    return row?.dataset?.cartGiftLine === 'true';
  }

  function _itemFromChangeBody(body, cart) {
    const items = cart?.items || [];

    if (body.line) {
      const idx = parseInt(body.line, 10) - 1;
      if (idx >= 0 && idx < items.length) return items[idx];
    }

    if (body.id) {
      const idStr = String(body.id);
      const byKey = items.find(function (it) {
        return _norm(it.key) === _norm(idStr);
      });
      if (byKey) return byKey;

      return (
        items.find(function (it) {
          return String(it.variant_id) === idStr || String(it.id) === idStr;
        }) || null
      );
    }

    return null;
  }

  function _qtyForBundleItem(item, newQty) {
    if (newQty === 0) return 0;
    if (_isBundleFreeGift(item)) return null;
    return newQty;
  }

  function _setBundleUpdate(updates, item, newQty) {
    const qty = _qtyForBundleItem(item, newQty);
    if (qty === null) return false;
    updates[item.key] = qty;
    return true;
  }

  function _itemInBundle(item, bundleId, domKeys) {
    if (!item || !bundleId) return false;
    if (_bidFromItem(item) === bundleId) return true;
    if (domKeys && domKeys.has(_norm(item.key))) return true;
    return _keysForBundle(bundleId).has(_norm(item.key));
  }

  function _bidFromRow(el) {
    const row = el?.closest?.('line-item, tr.line-item, .cart-item');
    if (!row) return '';
    const fromAttr = String(row.dataset?.bundleId || '').trim();
    if (fromAttr) return fromAttr;
    const itemKey = row.dataset?.itemKey;
    if (itemKey) return _bidFromKey(itemKey);
    return '';
  }

  function _sectionId() {
    return window.themeVariables?.settings?.pageType === 'cart' ? 'main-cart' : 'mini-cart';
  }

  function _isCartPayload(data) {
    return data && (data.items || data.item_count !== undefined || data.sections);
  }

  function _dispatchCartRefresh(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart: cart },
      })
    );
    const isCartPage = window.themeVariables?.settings?.pageType === 'cart';
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart: cart,
          openMiniCart: !isCartPage,
          replacementDelay: isCartPage ? 0 : 600,
        },
      })
    );
    setTimeout(_annotateRows, 50);
    setTimeout(_annotateRows, 700);
  }

  function _annotateRows() {
    document.querySelectorAll('line-item[data-item-key], tr.line-item[data-item-key]').forEach(function (row) {
      if (row.dataset.bundleId) return;
      const bid = _bidFromKey(row.dataset.itemKey);
      if (bid) row.dataset.bundleId = bid;
    });
  }

  function _rebuildRegistry(cart) {
    Object.keys(_cache).forEach(function (k) {
      delete _cache[k];
    });
    const byBundle = {};
    (cart.items || []).forEach(function (item) {
      const bid = _bidFromProps(item.properties);
      if (!bid || !item.key) return;
      _registerKey(item.key, bid);
      if (!byBundle[bid]) byBundle[bid] = [];
      byBundle[bid].push(_norm(item.key));
    });
    Object.keys(byBundle).forEach(function (bid) {
      _saveBundleKeys(bid, byBundle[bid]);
    });
    _annotateRows();
  }

  function refresh() {
    if (_refreshInFlight) return _refreshInFlight;
    _refreshInFlight = _getCart()
      .then(function (cart) {
        _rebuildRegistry(cart);
        return cart;
      })
      .catch(function () {
        return null;
      })
      .finally(function () {
        _refreshInFlight = null;
      });
    return _refreshInFlight;
  }

  async function _postCartUpdates(updates) {
    if (!updates || !Object.keys(updates).length) return null;

    const payload = { updates: updates };
    const sectionId = _sectionId();
    if (sectionId) payload.sections = [sectionId];

    const updated = await _post(_updateUrl(), payload);

    if (_isCartPayload(updated)) {
      _rebuildRegistry(updated);
      _dispatchCartRefresh(updated);
      return updated;
    }

    return null;
  }

  async function removeCartLine(itemKey) {
    if (_busy) return null;

    const nk = _norm(itemKey);
    if (!nk) return null;

    _busy = true;

    const miniCart = document.getElementById('mini-cart');
    if (miniCart) miniCart.classList.add('mini-cart--updating');

    try {
      const cart = await _getCart();
      const item = (cart.items || []).find(function (it) {
        return _norm(it.key) === nk;
      });
      if (!item) return null;

      const updates = {};
      updates[item.key] = 0;

      const updated = await _postCartUpdates(updates);
      if (updated) {
        try {
          sessionStorage.removeItem(KEY_PREFIX + nk);
        } catch (_) {
          /* ignore */
        }
        delete _cache[nk];
      }
      return updated;
    } catch (err) {
      console.error('[bundle-cart] remove line error', err);
      return null;
    } finally {
      _busy = false;
      if (miniCart) miniCart.classList.remove('mini-cart--updating');
    }
  }

  async function bulkUpdate(bundleId, newQty) {
    if (_busy || !bundleId) return null;

    _busy = true;

    const miniCart = document.getElementById('mini-cart');
    if (miniCart) miniCart.classList.add('mini-cart--updating');

    try {
      const cart = await _getCart();
      const items = cart.items || [];
      const domKeys = _keysFromDom(bundleId);
      const updates = {};
      let touched = 0;

      items.forEach(function (item) {
        if (!_itemInBundle(item, bundleId, domKeys)) return;
        if (_setBundleUpdate(updates, item, newQty)) touched += 1;
      });

      if (!touched) {
        domKeys.forEach(function (key) {
          const item = items.find(function (it) {
            return _norm(it.key) === key;
          });
          if (item && _setBundleUpdate(updates, item, newQty)) touched += 1;
        });
      }

      if (!touched) {
        _keysForBundle(bundleId).forEach(function (key) {
          const item = items.find(function (it) {
            return _norm(it.key) === key;
          });
          if (item && _setBundleUpdate(updates, item, newQty)) touched += 1;
        });
      }

      if (!touched) return null;

      const updated = await _postCartUpdates(updates);

      if (updated && newQty === 0) _clearBundle(bundleId);
      return updated;
    } catch (err) {
      console.error('[bundle-cart] bulk update error', err);
      return null;
    } finally {
      _busy = false;
      if (miniCart) miniCart.classList.remove('mini-cart--updating');
    }
  }

  async function resolveBundleIdFromChangeBody(body, cart) {
    const items = cart?.items || [];

    if (body.line) {
      const idx = parseInt(body.line, 10) - 1;
      if (idx >= 0 && idx < items.length) {
        const bid = _bidFromItem(items[idx]);
        if (bid) return bid;
      }
    }

    if (body.id) {
      const idStr = String(body.id);
      const byKey = items.find(function (it) {
        return _norm(it.key) === _norm(idStr);
      });
      if (byKey) {
        const bid = _bidFromItem(byKey);
        if (bid) return bid;
      }
      const byVariant = items.find(function (it) {
        return String(it.variant_id) === idStr || String(it.id) === idStr;
      });
      if (byVariant) {
        const bid = _bidFromItem(byVariant);
        if (bid) return bid;
      }
      const fromStore = _bidFromKey(idStr);
      if (fromStore) return fromStore;
    }

    return '';
  }

  function _isSellingPlanMutation(body) {
    return (
      Object.prototype.hasOwnProperty.call(body, 'selling_plan') ||
      Object.prototype.hasOwnProperty.call(body, 'selling_plan_id')
    );
  }

  function registerBundle(bundleId, cartItems) {
    if (!bundleId) return;
    const keys = [];
    (cartItems || []).forEach(function (item) {
      if (!item?.key) return;
      keys.push(_norm(item.key));
      _registerKey(item.key, bundleId);
    });
    _saveBundleKeys(bundleId, keys);
    _annotateRows();
  }

  function _handleBundleAction(e, bundleId, newQty) {
    if (!bundleId) return false;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    bulkUpdate(bundleId, newQty);
    return true;
  }

  window.BundleCart = {
    bundleIdFromItem: _bidFromItem,
    isBundleFreeGift: _isBundleFreeGift,
    isCartGiftLine: _isCartGiftLine,
    refresh: refresh,
    bulkUpdate: bulkUpdate,
    removeCartLine: removeCartLine,
    removeBundle: function (bundleId) {
      return bulkUpdate(bundleId, 0);
    },
    registerBundle: registerBundle,
    resolveBundleId: async function (hint) {
      if (hint?.bundleId) return hint.bundleId;
      if (hint?.row) {
        const fromRow = _bidFromRow(hint.row);
        if (fromRow) return fromRow;
      }
      if (hint?.itemKey) {
        const fromKey = _bidFromKey(hint.itemKey);
        if (fromKey) return fromKey;
      }
      const cart = await _getCart();
      if (hint?.line) {
        const item = cart.items[parseInt(hint.line, 10) - 1];
        return _bidFromItem(item);
      }
      return '';
    },
  };

  refresh();

  function _isCompleteCartPayload(cart) {
    return Boolean(cart && Array.isArray(cart.items) && cart.item_count !== undefined);
  }

  document.documentElement.addEventListener(
    'cart:updated',
    function (e) {
      const cart = e.detail?.cart;
      if (_isCompleteCartPayload(cart)) {
        _rebuildRegistry(cart);
        return;
      }
      refresh();
    },
    true
  );

  document.documentElement.addEventListener(
    'cart:refresh',
    function () {
      setTimeout(_annotateRows, 100);
    },
    true
  );

  document.addEventListener(
    'click',
    function (e) {
      if (_busy) return;

      const abBtn = e.target.closest && e.target.closest('.ab-remove-btn');
      if (abBtn) {
        if (_isGiftRow(abBtn)) {
          const row = abBtn.closest('line-item, tr.line-item');
          const itemKey = row?.dataset?.itemKey;
          if (itemKey) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            removeCartLine(itemKey);
          }
          return;
        }

        const bundleId = _bidFromRow(abBtn);
        if (bundleId) {
          _handleBundleAction(e, bundleId, 0);
        }
        return;
      }

      const btn = e.target.closest && e.target.closest('a[href*="cart/change"]');
      if (!btn) return;

      const href = btn.getAttribute('href') || '';
      const idMatch = href.match(/[?&]id=([^&]+)/);
      const qtyMatch = href.match(/[?&]quantity=(\d+)/);

      let bundleId = _bidFromRow(btn);
      if (!bundleId && idMatch) {
        bundleId = _bidFromKey(decodeURIComponent(idMatch[1]));
      }

      if (idMatch && (!qtyMatch || parseInt(qtyMatch[1], 10) === 0)) {
        if (_isGiftRow(btn)) {
          const row = btn.closest('line-item, tr.line-item');
          const itemKey = row?.dataset?.itemKey || (idMatch ? decodeURIComponent(idMatch[1]) : '');
          if (itemKey) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            removeCartLine(itemKey);
          }
          return;
        }

        if (!bundleId) return;

        _handleBundleAction(e, bundleId, 0);
        return;
      }

      if (!bundleId) return;

      if (qtyMatch) {
        const newQty = parseInt(qtyMatch[1], 10);
        if (!isFinite(newQty)) return;
        _handleBundleAction(e, bundleId, newQty >= 1 ? newQty : 0);
      }
    },
    true
  );

  if (!window._nbbbFetchPatched) {
    window._nbbbFetchPatched = true;
    const origFetch = window.fetch.bind(window);

    window.fetch = async function (input, init) {
      if (_busy) return origFetch(input, init);

      const url = typeof input === 'string' ? input : input?.url || '';
      const method = String((init?.method || 'GET')).toUpperCase();

      if (method === 'POST' && url.includes('cart/change') && init?.body) {
        try {
          const body = JSON.parse(init.body);
          if (_isSellingPlanMutation(body)) {
            return origFetch(input, init);
          }

          const quantity = parseInt(body.quantity, 10);
          if (!isFinite(quantity)) return origFetch(input, init);

          const cart = await _getCart();
          const bundleId = await resolveBundleIdFromChangeBody(body, cart);
          const changedItem = _itemFromChangeBody(body, cart);

          if (bundleId && quantity === 0 && _isCartGiftLine(changedItem)) {
            const updated = await removeCartLine(changedItem.key);
            if (updated) {
              return new Response(JSON.stringify(updated), {
                status: 200,
                headers: { 'Content-Type': 'application/json' },
              });
            }
            return new Response(
              JSON.stringify({ status: 500, message: 'Gift line remove failed' }),
              { status: 500, headers: { 'Content-Type': 'application/json' } }
            );
          }

          if (bundleId) {
            const updated = await bulkUpdate(bundleId, quantity);
            if (updated) {
              return new Response(JSON.stringify(updated), {
                status: 200,
                headers: { 'Content-Type': 'application/json' },
              });
            }
            return new Response(
              JSON.stringify({ status: 500, message: 'Bundle cart update failed' }),
              { status: 500, headers: { 'Content-Type': 'application/json' } }
            );
          }
        } catch (_) {
          /* fall through */
        }
      }

      return origFetch(input, init);
    };
  }
})();
